import React, {useEffect} from "react";
import {Button, Descriptions, Spin, Tag} from "antd";
import {ArrowLeft, ShieldAlert} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {getAuditTrailById} from "../../redux/slice/auditTrailSlice";
import {HeroHeader, Card, COLORS} from "../../designSystem";

const riskColor = (value: string) => {
  if (value === "Critical") return "red";
  if (value === "High") return "volcano";
  return "green";
};

const statusColor = (status: number) => {
  if (status >= 500) return "red";
  if (status >= 400) return "orange";
  if (status >= 300) return "blue";
  return "green";
};

const dateText = (value: string) => value ? new Date(value).toLocaleString() : "-";
const text = (value: any) => value || "-";

const JsonBlock = ({value}: {value?: string}) => {
  if (!value) return <span>-</span>;

  let formatted = value;
  try {
    formatted = JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    formatted = value;
  }

  return (
    <pre className="max-h-96 overflow-auto rounded-lg bg-slate-950 p-4 text-xs text-white">
      {formatted}
    </pre>
  );
};

function AuditTrailDetails() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {token} = useSelector((state: RootState) => state.account);
  const {selectedAudit, loading} = useSelector((state: RootState) => state.auditTrail);
  const isCurrentAudit = selectedAudit && String(selectedAudit.id) === String(id);

  useEffect(() => {
    if (token && id) {
      dispatch(getAuditTrailById({token, auditId: id}));
    }
  }, [token, id, dispatch]);

  if (loading || !isCurrentAudit) {
    return <AdminLayout><div className="m-10"><Spin tip="Loading audit trail..."/></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <HeroHeader
          icon={ShieldAlert}
          title="Audit Details"
          subtitle={selectedAudit?.traceIdentifier || "Request journey"}
          actions={<Button icon={<ArrowLeft size={16}/>} onClick={() => navigate(-1)}>Back</Button>}
        />

        <Card className="mt-8 p-5">
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Status">
              <Tag color={statusColor(selectedAudit?.statusCode)}>{selectedAudit?.statusCode || "-"}</Tag>
              {selectedAudit?.isBlocked && <Tag color="red">Blocked</Tag>}
              {selectedAudit?.isError && <Tag color="orange">Error</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label="Risk">
              <Tag color={riskColor(selectedAudit?.riskLevel)}>{selectedAudit?.riskLevel || "Low"}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Risk Reason">{text(selectedAudit?.riskReason)}</Descriptions.Item>
            <Descriptions.Item label="Method">{text(selectedAudit?.httpMethod)}</Descriptions.Item>
            <Descriptions.Item label="Path">{text(selectedAudit?.path)}</Descriptions.Item>
            <Descriptions.Item label="Query String">{text(selectedAudit?.queryString)}</Descriptions.Item>
            <Descriptions.Item label="Duration">{selectedAudit?.durationMs || 0}ms</Descriptions.Item>
            <Descriptions.Item label="Created At">{dateText(selectedAudit?.createdAt)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="mt-6 p-5">
          <Descriptions title="Actor and Device" bordered column={1}>
            <Descriptions.Item label="User Email">{text(selectedAudit?.userEmail)}</Descriptions.Item>
            <Descriptions.Item label="User Name">{text(selectedAudit?.userName)}</Descriptions.Item>
            <Descriptions.Item label="Application User Id">{text(selectedAudit?.applicationUserId)}</Descriptions.Item>
            <Descriptions.Item label="IP Address">{text(selectedAudit?.ipAddress)}</Descriptions.Item>
            <Descriptions.Item label="User Agent">{text(selectedAudit?.userAgent)}</Descriptions.Item>
            <Descriptions.Item label="Trace Id">{text(selectedAudit?.traceIdentifier)}</Descriptions.Item>
            <Descriptions.Item label="Correlation Id">{text(selectedAudit?.correlationId)}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">Request Body</div>
          <JsonBlock value={selectedAudit?.requestBody}/>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">Response Body</div>
          <JsonBlock value={selectedAudit?.responseBody}/>
        </Card>

        <Card className="mt-6 p-5">
          <div className="mb-3 text-lg font-semibold">Error Message</div>
          <JsonBlock value={selectedAudit?.errorMessage}/>
        </Card>
      </main>
    </AdminLayout>
  );
}

export default AuditTrailDetails;
